import React from 'react';
import { useField } from 'informed';
import {
  FormFeedback,
  FormInputFilesList,
  FormInputFileUpload,
  FormItem,
  FormLabel,
  Icon,
  useFileUploadState,
} from '@tesla/design-system-react';
import { iconErrorSmallFilled } from '@tesla/design-system-icons';
import _merge from 'lodash/merge';
import { useIntl } from 'react-intl';
import isEmpty from 'lodash/isEmpty';
import messages from './messages';
import { logSentryError } from '../../../utils/sentry';

const FileUpload = ({ ...props }) => {
  const intl = useIntl();
  const {
    fileNames,
    fileSelectErrors,
    handleInputChange,
    handleItemRemove,
    inputRef,
    setFileSelectErrors,
  } = useFileUploadState();
  const { formatMessage } = intl;
  // eslint-disable-next-line react/prop-types
  const { errorMessage = {} } = props;
  const { fieldApi, fieldState, userProps } = useField(props);
  const { showError, error, value } = fieldState;
  const { setValue, getValue, setTouched, reset } = fieldApi;
  const {
    label,
    accept = [],
    buttonText = formatMessage(messages.FileUploadButtonText),
    helperText = '',
    maxFiles = 1,
    maxFileSize = 1000000,
    required = false,
    ...inputProps
  } = userProps;
  const acceptWithPeriod = accept.map((extension) => `.${extension}`);

  // Merge in file names from state if unable to retrieve from input.
  if (isEmpty(fileNames) && !isEmpty(value)) {
    if (maxFiles > 1) {
      Object.keys(value).forEach((v) => {
        fileNames.push(v.filename);
      });
    } else {
      fileNames.push(value.filename);
    }
  }

  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
  };

  const defaultErrorMessage = {
    uploadFailed: formatMessage(messages.FileUploadUploadFailed),
    invalidFileType: formatMessage(messages.FileUploadInvalidFileType),
    maxFiles: formatMessage(messages.FileUploadFilesExceeded, {
      max: formatBytes(maxFiles),
    }),
    maxFileSize: formatMessage(messages.FileUploadFileSizeExceeded, {
      max: formatBytes(maxFileSize),
    }),
  };
  const errorMessages = _merge(defaultErrorMessage, errorMessage || {});

  const getFileAsBase64 = (fileInput) =>
    new Promise((resolve, reject) => {
      const fileReader = new FileReader();

      fileReader.onload = (res) => {
        resolve(res.target.result);
      };
      fileReader.onerror = (err) => {
        logSentryError(err);
        reject(err);
      };

      fileReader.readAsDataURL(fileInput);
    });

  const handleError = () => {
    if (fileSelectErrors) {
      return (
        <>
          {fileSelectErrors.uploadFailed && errorMessages.uploadFailed}
          {fileSelectErrors.invalidFileType && errorMessages.invalidFileType}
          {fileSelectErrors.maxFileSizeExceeded && errorMessages.maxFileSize}
          {fileSelectErrors.maxFilesExceeded && errorMessages.maxFiles}
        </>
      );
    }
    return <>{error && error}</>;
  };

  const isValidFileType = (file) => {
    const fileName = file.name;
    const fileExtension = fileName.split('.').pop();

    return accept.includes(fileExtension);
  };

  const handleFileChange = async () => {
    const fileInput = inputRef.current;
    if (!fileInput || !fileInput.files) {
      return;
    }

    if (fileInput.files.length === 0) {
      reset();
      return;
    }

    // eslint-disable-next-line no-restricted-syntax
    for (const i of fileInput.files) {
      const file = fileInput.files.item(i);

      if (!isValidFileType(file)) {
        setFileSelectErrors({ invalidFileType: true });
        // eslint-disable-next-line no-continue
        continue;
      }

      try {
        // eslint-disable-next-line no-await-in-loop
        const fileDataUrl = await getFileAsBase64(file);

        handleInputChange();
        if (maxFiles > 1) {
          setValue([
            ...getValue(),
            {
              filename: file.name,
              value: fileDataUrl,
            },
          ]);
        } else {
          setValue({
            ...getValue(),
            filename: file.name,
            value: fileDataUrl,
          });
        }
      } catch (e) {
        setFileSelectErrors({ uploadFailed: true });
      }
    }
  };

  return (
    <FormItem error={showError || !!fileSelectErrors} variant="file-upload">
      <FormLabel caption={helperText}>{label}</FormLabel>
      {(showError || !!fileSelectErrors) && (
        <FormFeedback
          className="tds-form-item--outdent"
          feedbackText={
            <>
              <Icon data={iconErrorSmallFilled} />
              {handleError()}
            </>
          }
        />
      )}
      <FormInputFilesList
        fileNames={fileNames}
        onRemove={async (e) => {
          handleItemRemove(e);
          await handleFileChange();
        }}
      />
      <FormInputFileUpload
        variant="tertiary"
        accept={acceptWithPeriod.join(',')}
        inputRef={inputRef}
        label={buttonText}
        maxFileSize={maxFileSize}
        maxFiles={maxFiles}
        onChange={async () => {
          await handleFileChange();
        }}
        onBlur={() => {
          setTouched(true);
        }}
        required={required}
        {...inputProps}
        type="file"
      />
    </FormItem>
  );
};

export default FileUpload;
